import 'leaflet'
// IMPORTANT: Keep this import before others. Do not remove this comment.
// Reason: Leaflet must be loaded before Geoman.
import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'

import { GeoJsonObject } from 'geojson'
import L from 'leaflet'
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { GeoJSON } from 'react-leaflet'
import { useUpdateEffect } from 'usehooks-ts'

import { Map as MapContainer } from '@/components/map'
import { CogTileLayerProxy } from '@/components/map/cog-tile-layer/proxy'
import { GeoTiff } from '@/pages/demands/steps/resume/components/geo-tiff'
import { GeoJSONObject } from '@/types/geospatial'
import { geoJSONToShapefile, shpFilesToZip } from '@/utils/file-conversions'
import { getInfosFromGeoJSON } from '@/utils/tiff-manipulations'

import { EditableButton } from '../editable-button'
import { ExportButton } from '../export-button'
import { VisualizationControlButton } from '../visualization-control-button'
import { useStyles } from './styles'

export interface MapHandle {
  getShapefileFiles: () => Promise<{
    geojson: GeoJsonObject
    results: {
      centroid: [number, number]
      filename: string
      interestArea: number
    }
    files: File[]
    zip: Blob
  }>
}

interface MapProps {
  perimeter?: GeoJsonObject & { fileName?: string }
  thumbnail?: File
  cogUrl?: string
  editable?: boolean
  editableButton?: boolean
  disabledEditableButton?: boolean
}

export const Map = forwardRef<MapHandle, MapProps>(function Map(
  {
    perimeter,
    thumbnail,
    cogUrl,
    editable = false,
    editableButton = false,
    disabledEditableButton = false,
  },
  ref,
) {
  const classes = useStyles()
  const mapRef = useRef<L.Map | null>(null)

  const [isGeoTiffReady, setIsGeoTiffReady] = useState(false)
  const [showStates, setShowStates] = useState({
    perimeters: true,
    mosaic: true,
    map: true,
  })

  const bounds = useMemo(
    () => perimeter && L.geoJSON(perimeter).getBounds().pad(0.2),
    [perimeter],
  )

  const handleMapCreated = useCallback((map: L.Map) => {
    map.invalidateSize()
    map.setMinZoom(14)
    map.setMaxZoom(24)
    mapRef.current = map

    if (!editable) return

    map.pm.setLang('pt_br')
    map.pm.addControls({
      position: 'topright',

      drawPolygon: true,
      editMode: true,
      dragMode: true,
      cutPolygon: true,
      removalMode: true,

      drawMarker: false,
      drawCircle: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawText: false,
    })
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      getShapefileFiles: async () => {
        if (!mapRef.current) {
          throw new Error('Map is not initialized')
        }

        const featureCollection = mapRef.current.pm
          .getGeomanLayers(true)
          .toGeoJSON()

        if (featureCollection.type !== 'FeatureCollection') {
          throw new Error('Invalid GeoJSON type')
        }

        const geojson = {
          ...featureCollection,
          fileName: perimeter?.fileName,
          features: featureCollection.features.map((feature) => ({
            ...feature,
            properties: {},
          })),
        }

        const { bounds, filename, interestArea } = await getInfosFromGeoJSON(
          geojson as GeoJSONObject,
        )

        const files = await geoJSONToShapefile(geojson, filename)
        const zip = await shpFilesToZip(files, {
          type: 'blob',
          filename: filename,
        })

        return {
          geojson,
          results: {
            centroid: [bounds.lat, bounds.lng],
            filename,
            interestArea,
          },
          files,
          zip,
        }
      },
    }),
    [],
  )

  useUpdateEffect(() => {
    if (!mapRef.current || !bounds) return

    mapRef.current.flyToBounds(bounds, {
      maxZoom: 20,
      animate: true,
      duration: 0.5,
    })
  }, [bounds])

  return (
    <div className={classes.mapContainer}>
      <MapContainer
        zoomSnap={1}
        bounds={bounds}
        maxBounds={bounds}
        whenCreated={handleMapCreated}
        opacity={isGeoTiffReady && showStates.mosaic ? 0.5 : 1}
        disabledBaseLayer={!showStates.map}
        disabledControls={{
          attributionControl: true,
          scaleControl: true,
          baseLayerControl: true,
        }}
      >
        {!editable && (
          <>
            {editableButton && (
              <EditableButton
                title='Editar perímetro'
                disabled={disabledEditableButton}
              />
            )}
            <ExportButton />
          </>
        )}

        <VisualizationControlButton
          states={showStates}
          onShowChange={(type, checked) => {
            setShowStates((prev) => ({ ...prev, [type]: checked }))
          }}
        />
        {!thumbnail && cogUrl ? (
          <CogTileLayerProxy
            url={cogUrl}
            onReady={() => setIsGeoTiffReady(true)}
            opacity={showStates.mosaic ? 1 : 0}
          />
        ) : (
          <GeoTiff
            file={thumbnail}
            onReady={(ready) => setIsGeoTiffReady(ready ?? false)}
            isShow={showStates.mosaic}
          />
        )}
        {perimeter && (
          <GeoJSON
            data={perimeter}
            style={{
              color: 'white',
              weight: 1.5,
              fillColor: 'white',
              interactive: true,
              fillOpacity: showStates.perimeters ? 0.5 : 0,
              opacity: showStates.perimeters ? 1 : 0,
              fillRule: 'nonzero',
            }}
          />
        )}
      </MapContainer>
    </div>
  )
})
